import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, makeStyles } from '@material-ui/core';

const BODY_TYPE = {
    BOLD: 'bold',
    NORMAL: 'normal',
    EXAMPLE: 'example',
};

const useStyles = makeStyles((theme, { placement = 'top' } = {}) => {
    const isHorizontal = placement === 'left' || placement === 'right';
    return {
        tooltip: {
            position: 'relative',
            maxWidth: 320,
            padding: 0,
            backgroundColor: theme.palette.background.white,
            color: theme.palette.text.primary,
            fontSize: 13,
            lineHeight: 1.5,
            boxShadow: theme.shadows[4],
            borderRadius: 4,
            overflow: 'visible',
        },
        arrow: {
            color: theme.palette.background.white,
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))',
            width: isHorizontal ? 'calc(0.71em + 4px)' : 'calc(1em + 4px)',
            height: isHorizontal ? 'calc(1em + 4px)' : 'calc(0.71em + 4px)',
        },
        content: {
            padding: '12px 14px',
        },
        header: {
            fontWeight: 700,
            fontSize: 13,
            marginBottom: 8,
            paddingBottom: 8,
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
        },
        body: {
            '& p': {
                margin: '0 0 6px 0',
                '&:last-child': {
                    marginBottom: 0,
                },
            },
        },
        bodyText: {
            fontWeight: 400,
        },
        bodyTextBold: {
            fontWeight: 700,
        },
        exampleBlock: {
            marginTop: 8,
            marginBottom: 8,
            padding: '10px 12px 10px 14px',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderLeft: '4px solid #1976d2',
            borderRadius: '0 4px 4px 0',
        },
        exampleLabel: {
            fontWeight: 700,
            marginRight: 4,
        },
    };
});

function FormattedTooltip({
    header,
    body,
    placement,
    children,
    classes: classesProp,
    ...tooltipProps
}) {
    const classes = useStyles({ placement: placement || 'top' });
    const bodyContent = Array.isArray(body)
        ? body.map((item, index) => {
            const key = `segment-${index}`;
            const text = typeof item === 'string' ? item : (item && item.text);
            if (text == null) return null;
            if (item && item.type === BODY_TYPE.EXAMPLE) {
                return (
                    <div key={key} className={classes.exampleBlock}>
                        <span className={classes.exampleLabel}>EXAMPLE: </span>
                        <span className={classes.bodyText}>{text}</span>
                    </div>
                );
            }
            const isBold = item && item.type === BODY_TYPE.BOLD;
            return (
                <span
                    key={key}
                    className={isBold ? classes.bodyTextBold : classes.bodyText}
                >
                    {text}
                </span>
            );
        })
        : body;

    const title = (
        <div className={classes.content}>
            {header && (
                <div className={classes.header}>
                    {header}
                </div>
            )}
            <div className={classes.body}>
                {bodyContent}
            </div>
        </div>
    );

    return (
        <Tooltip
            title={title}
            placement={placement}
            arrow
            classes={{
                ...classesProp,
                tooltip: [classes.tooltip, classesProp?.tooltip].filter(Boolean).join(' '),
                arrow: [classes.arrow, classesProp?.arrow].filter(Boolean).join(' '),
            }}
            {...tooltipProps}
        >
            {children}
        </Tooltip>
    );
}

FormattedTooltip.BODY_TYPE = BODY_TYPE;

FormattedTooltip.propTypes = {
    header: PropTypes.string,
    body: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.string,
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            type: PropTypes.oneOf([BODY_TYPE.BOLD, BODY_TYPE.NORMAL, BODY_TYPE.EXAMPLE]),
        })),
    ]).isRequired,
    placement: PropTypes.string,
    children: PropTypes.node.isRequired,
    classes: PropTypes.object,
};

FormattedTooltip.defaultProps = {
    header: null,
    placement: 'top',
    classes: {},
};

export default FormattedTooltip;
